/* eslint-disable no-unused-expressions */
import { toast } from "react-toastify";

export const DEFAULT_LOCATION_TYPES = [
    { label: "Branch", value: "branch" },
    { label: "Stockist", value: "stockist" },
    { label: "Warehouse", value: "warehouse" },
    { label: "Office", value: "office" },
    { label: "Click And Collect", value: "click_and_collect" },
];

export const LOCATION_TYPE_OPTIONS = [
    { label: "Locations", value: "location" },
    { label: "Location Types", value: "location_type" },
    { label: "Location Features", value: "location_feature" },
];

export const IBT_SAVE_ERROR_TOAST =
    "Error: See IBT Config Sequences section for detailed error(s)";

export const isIbtConfigViolation = (violation) =>
    violation.propertyPath === "ibtConfig" ||
    violation.propertyPath.startsWith("ibtConfig[");

export const getIbtConfigViolations = (violations = []) =>
    violations.filter(isIbtConfigViolation);

const getNonIbtViolationMessage = (violations = []) => {
    const messages = violations
        .filter((violation) => !isIbtConfigViolation(violation))
        .map((violation) => violation.message)
        .filter(Boolean);

    if (messages.length === 0) {
        return null;
    }

    return [...new Set(messages)].join(" ");
};

export const setViolationFieldErrors = (setFieldError, violations = []) => {
    const messagesByPath = violations
        .filter((violation) => violation.propertyPath !== "ibtConfig")
        .reduce((acc, violation) => {
            acc[violation.propertyPath] = acc[violation.propertyPath]
                ? `${acc[violation.propertyPath]} ${violation.message}`
                : violation.message;
            return acc;
        }, {});

    Object.entries(messagesByPath).forEach(([propertyPath, message]) => {
        setFieldError(propertyPath, message);
    });
};

export const showLocationSaveErrorToasts = (error, violations = []) => {
    const ibtConfigViolations = getIbtConfigViolations(violations);
    const nonIbtMessage = getNonIbtViolationMessage(violations);

    if (ibtConfigViolations.length > 0) {
        toast.error(IBT_SAVE_ERROR_TOAST);
    }

    if (nonIbtMessage) {
        toast.error(nonIbtMessage);
    } else if (ibtConfigViolations.length === 0) {
        toast.error(
            error?.error?.body?.hasOwnProperty("hydra:description")
                ? error.error.body["hydra:description"]
                : "Whoops, there was a problem..."
        );
    }
};

export const formatDataForForm = (data) => {
    const types =
        data?.locationTypes?.reduce((d, t) => [...d, t.type], []) || [];

    const formLocationFeatures = {};

    if (data?.locationFeatures?.length > 0) {
        data?.locationFeatures?.forEach((feature) => {
            !!formLocationFeatures[feature["feature"]];
        });
    }

    const ibtConfig =
        data?.ibtConfig?.map((sequence) => ({
            timeRangeStart: sequence.timeRangeStart || "",
            timeRangeEnd: sequence.timeRangeEnd || "",
            daysOfWeek: sequence.daysOfWeek
                ? sequence.daysOfWeek
                      .split(",")
                      .filter((day) => day !== "")
                      .map((day) => parseInt(day, 10))
                : [],
            branchSequence: sequence.branchSequence || [],
        })) || [];

    return {
        ...data,
        locationTypes: types,
        formLocationFeatures,
        ibtConfig,
    };
};

export const canEditIbtConfig = (user) => {
    const userGroup = user?.profile?.user_group || "";

    return (
        typeof userGroup === "string" &&
        userGroup.trim().toLowerCase() === "admin"
    );
};

const formatIbtConfigForApi = (ibtConfig) =>
    (ibtConfig || []).map((sequence) => ({
        timeRangeStart: sequence.timeRangeStart,
        timeRangeEnd: sequence.timeRangeEnd,
        daysOfWeek: Array.isArray(sequence.daysOfWeek)
            ? sequence.daysOfWeek.join(",")
            : sequence.daysOfWeek,
        branchSequence: sequence.branchSequence || [],
    }));

const shouldSendIbtConfig = (ibtConfig, { canEditIbt, initialIbtConfig }) => {
    if (!canEditIbt) {
        return false;
    }

    if (initialIbtConfig === undefined) {
        return true;
    }

    return (
        JSON.stringify(formatIbtConfigForApi(ibtConfig)) !==
        JSON.stringify(formatIbtConfigForApi(initialIbtConfig))
    );
};

export const formatDataForApi = (
    data,
    { canEditIbt = false, initialIbtConfig } = {}
) => {
    const types =
        data?.locationTypes?.reduce((d, t) => [...d, { type: t }], []) || [];
    const features = [];

    if (data?.formLocationFeatures) {
        for (const [key, val] of Object.entries(data.formLocationFeatures)) {
            if (val === true) {
                features.push({ feature: key });
            }
        }

        delete data.formLocationFeatures;
    }

    const dataClone = {
        ...data,
        locationTypes: types,
        locationFeatures: features,
        locationPageStatus: data.locationPageStatus || "active",
        longitude: parseFloat(data.longitude),
        latitude: parseFloat(data.latitude),
        ecpBranchId: parseInt(data.ecpBranchId),
    };

    if (
        shouldSendIbtConfig(data?.ibtConfig, { canEditIbt, initialIbtConfig })
    ) {
        dataClone.ibtConfig = formatIbtConfigForApi(data?.ibtConfig);
    } else {
        delete dataClone.ibtConfig;
    }

    if (data?.locationImages?.length > 0) {
        dataClone.locationImages = data.locationImages.map((image, idx) => {
            return {
                ...image,
                sortOrder: idx + 1,
            };
        });
    }

    if (!!data?.email === false) {
        dataClone.email = null;
    }

    return dataClone;
};
